import type {ChatState, ModelSelection} from '@microsoft/agent-host-protocol'
import {getTurns} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'

/**
 * A client-side timeline notice recording that the user switched the model or
 * reasoning effort mid-conversation, surfaced inline to match the Copilot
 * Desktop app. Anchored to the turn it followed so it interleaves with turns as
 * the conversation grows.
 */
export interface ModelChangeMarker {
  /** Stable id for React keys and ordering. */
  id: string
  /** Renders after the timeline turn with this id; `null` when the change happened before any turn. */
  afterTurnId: string | null
  /** The model in effect after the change; drives the vendor icon and, for `model` changes, the name. */
  modelId: string | undefined
  kind: 'model' | 'reasoning'
  /** For `model` changes: the reasoning effort now in effect (shown muted after the model name). */
  reasoningEffort?: ReasoningEffort
  /** For `reasoning` changes: the previous reasoning effort. */
  fromReasoningEffort?: ReasoningEffort
  /** For `reasoning` changes: the new reasoning effort. */
  toReasoningEffort?: ReasoningEffort
}

function reasoningEffort(model: ModelSelection | undefined): ReasoningEffort | undefined {
  const value = model?.config?.reasoningEffort
  return isReasoningEffort(value) ? value : undefined
}

/**
 * Projects model transitions from the model selection recorded on each AHP turn.
 * The first turn establishes the baseline and is not itself a change.
 */
export function projectModelChangeMarkers(state: ChatState | undefined): ModelChangeMarker[] {
  if (!state) return []

  const turns = getTurns(state)
  const markers: ModelChangeMarker[] = []

  for (let index = 1; index < turns.length; index++) {
    const previousTurn = turns[index - 1]
    const currentTurn = turns[index]
    if (!previousTurn || !currentTurn) continue
    const previousModel = previousTurn.message?.model
    const model = currentTurn.message?.model
    if (!previousModel?.id || !model?.id) continue
    const previousReasoningEffort = reasoningEffort(previousModel)
    const currentReasoningEffort = reasoningEffort(model)

    if (model.id !== previousModel.id) {
      markers.push({
        id: `model-change-${currentTurn.id}`,
        afterTurnId: previousTurn.id,
        kind: 'model',
        modelId: model.id,
        reasoningEffort: currentReasoningEffort,
      })
    } else if (currentReasoningEffort !== previousReasoningEffort) {
      markers.push({
        id: `model-change-${currentTurn.id}`,
        afterTurnId: previousTurn.id,
        kind: 'reasoning',
        modelId: model.id,
        fromReasoningEffort: previousReasoningEffort,
        toReasoningEffort: currentReasoningEffort,
      })
    }
  }

  return markers
}
